/**
 * Money is a bigint count of ten-thousandths of a currency unit (4 decimal places),
 * matching numeric(19,4) in Postgres. Never use floats for money.
 */
export type Money = bigint;

export const SCALE = 4;
const FACTOR = 10_000n;

/** One cent expressed in Money units. */
export const CENT: Money = 100n;

const MONEY_PATTERN = /^(-)?(\d+)(?:\.(\d{1,4}))?$/;

/** Parse a decimal string such as "123.45" (up to 4 decimal places). */
export function parseMoney(input: string): Money {
  const match = MONEY_PATTERN.exec(input.trim());
  if (!match) {
    throw new RangeError(`parseMoney: invalid amount "${input}"`);
  }
  const [, sign, whole, fraction = ''] = match;
  const units = BigInt(whole as string) * FACTOR + BigInt(fraction.padEnd(SCALE, '0'));
  return sign ? -units : units;
}

/** Format as a plain decimal string with 4 decimal places, as Postgres numeric(19,4) expects. */
export function formatMoney(amount: Money): string {
  const negative = amount < 0n;
  const abs = negative ? -amount : amount;
  const whole = abs / FACTOR;
  const fraction = (abs % FACTOR).toString().padStart(SCALE, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/** Format for display with 2 decimal places. Throws if the amount is not a whole number of cents. */
export function formatCents(amount: Money): string {
  if (amount % CENT !== 0n) {
    throw new RangeError(`formatCents: ${formatMoney(amount)} is not a whole number of cents`);
  }
  return formatMoney(amount).slice(0, -2);
}

export function sum(amounts: readonly Money[]): Money {
  let total = 0n;
  for (const amount of amounts) total += amount;
  return total;
}

const RATE_PATTERN = /^(\d{1,10})(?:\.(\d{1,8}))?$/;
const RATE_SCALE = 8;
const RATE_FACTOR = 100_000_000n;

/** Parse an exchange rate such as "0.0538" (positive, up to 8 decimal places) into units of 10^-8. */
export function parseRate(input: string): bigint {
  const match = RATE_PATTERN.exec(input.trim());
  if (!match) throw new RangeError(`parseRate: invalid rate "${input}"`);
  const [, whole, fraction = ''] = match;
  const units = BigInt(whole as string) * RATE_FACTOR + BigInt(fraction.padEnd(RATE_SCALE, '0'));
  if (units === 0n) throw new RangeError('parseRate: a rate must be positive');
  return units;
}

/** A rate (units of 10^-8) as a decimal string without trailing zeros, e.g. "0.0538". */
export function formatRate(units: bigint): string {
  const whole = units / RATE_FACTOR;
  const fraction = (units % RATE_FACTOR).toString().padStart(RATE_SCALE, '0').replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : `${whole}`;
}

/**
 * Convert an amount at an exchange rate (a decimal string with up to 8 places), rounded half away
 * from zero to the cent. Pure bigint arithmetic: amount (10^-4 units) times rate (10^-8 units) is
 * in 10^-12 units, divided by 10^10 into whole cents, then scaled back to Money.
 */
export function convertMoney(amount: Money, rate: string): Money {
  const product = amount * parseRate(rate);
  const divisor = RATE_FACTOR * CENT;
  const negative = product < 0n;
  const abs = negative ? -product : product;
  let cents = abs / divisor;
  if ((abs % divisor) * 2n >= divisor) cents += 1n;
  return (negative ? -cents : cents) * CENT;
}
